// Phase 34c.3 — investor portal partnership card.
//
// Premium card representation of a single row from
// GET /api/syndication/me/partnerships. Surfaces the values that matter
// to an LP at a glance: deal address (the hero), sponsor name + status
// pill, ownership %, committed, and cumulative pref + capital returned.

import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";

function StatusPill({ status }) {
  const palette = {
    draft:    "bg-slate-100 text-slate-700",
    open:     "bg-amber-50 text-amber-700",
    funded:   "bg-indigo-50 text-indigo-700",
    closed:   "bg-slate-100 text-slate-500",
  };
  const cls = palette[status] || palette.draft;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {(status || "draft").toUpperCase()}
    </span>
  );
}

export default function PartnershipCard({ partnership, fmt }) {
  const p = partnership;
  return (
    <Link
      to={`/syndication/partnerships/${p.partnership_id}`}
      data-testid={`investor-partnership-card-${p.partnership_id}`}
      className="group relative flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md hover:ring-2 hover:ring-indigo-100"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-base font-semibold text-slate-900">
            {p.partnership_name || "Partnership"}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{p.deal_address || "No address on file"}</span>
          </div>
        </div>
        <StatusPill status={p.status} />
      </div>

      {p.sponsor_name ? (
        <div className="text-xs text-slate-500">
          Sponsored by{" "}
          <span className="font-medium text-slate-700">{p.sponsor_name}</span>
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Ownership
          </div>
          <div className="mt-0.5 text-base font-semibold tabular-nums text-slate-900">
            {Number(p.my_ownership_pct || 0).toFixed(2)}%
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Committed
          </div>
          <div className="mt-0.5 text-base font-semibold tabular-nums text-slate-900">
            {fmt.money(p.my_committed_amount)}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Pref received
          </div>
          <div className="mt-0.5 text-sm font-medium tabular-nums text-emerald-700">
            {fmt.money(p.my_cumulative_pref_paid)}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Capital back
          </div>
          <div className="mt-0.5 text-sm font-medium tabular-nums text-violet-700">
            {fmt.money(p.my_cumulative_capital_returned)}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-slate-100 pt-3 text-xs text-slate-500">
        <span>
          {p.last_distribution_at
            ? `Last distribution ${new Date(p.last_distribution_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
            : "No distributions yet"}
        </span>
        <span className="inline-flex items-center gap-1 font-medium text-indigo-600 transition-transform group-hover:translate-x-0.5">
          Open <ArrowUpRight className="h-3 w-3" />
        </span>
      </div>
    </Link>
  );
}
